import { useState } from 'react'
import { CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Progress } from '@/components/ui/progress'
import { MAX_SITE_PAGES, type SiteImportPage, type SitePage, type SitePagesResult } from '@/api/knowledgePages'

/**
 * What the site import of an existing agent and the creation assistant share:
 * the pages to tick, then the work of the server, page by page.
 */

/** Same page whatever the protocol, "www." or trailing slash. */
export const samePageKey = (url: string) =>
  url
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '')

const FINISHED: SiteImportPage['etat'][] = ['prete', 'enregistree', 'deja', 'echec']

/**
 * The ticked pages: the most useful ones the agent does not have yet until the
 * user ticks a box. Worked out at each render: the agent's pages may load after the search.
 */
export function usePageSelection(found: SitePagesResult | null, isExisting: (url: string) => boolean) {
  const [picked, setPicked] = useState<string[] | null>(null)
  const selected = (
    picked ?? (found?.pages ?? []).filter((p) => !isExisting(p.url)).slice(0, MAX_SITE_PAGES).map((p) => p.url)
  ).filter((url) => !isExisting(url))
  return {
    selected,
    toggle: (url: string, checked: boolean) =>
      setPicked(checked ? [...selected, url] : selected.filter((u) => u !== url)),
    reset: () => setPicked(null),
  }
}

export function SitePagesChecklist({
  pages,
  selected,
  isExisting = () => false,
  onToggle,
}: {
  pages: SitePage[]
  selected: string[]
  isExisting?: (url: string) => boolean
  onToggle: (url: string, checked: boolean) => void
}) {
  const { t } = useTranslation()
  const full = selected.length >= MAX_SITE_PAGES
  return (
    <div className="min-w-0 space-y-3">
      <ul className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
        {pages.map((p) => {
          const already = isExisting(p.url)
          const checked = selected.includes(p.url)
          return (
            <li key={p.url}>
              <label className={`flex items-start gap-3 py-2.5 ${already ? 'opacity-60' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={already || (!checked && full)}
                  onChange={(e) => onToggle(p.url, e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 rounded accent-blue-600 disabled:opacity-40"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">{p.titre}</span>
                  <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                    {already ? t('manager.aiAgents.siteImport.alreadyAdded') : pathOf(p.url)}
                  </span>
                </span>
              </label>
            </li>
          )
        })}
      </ul>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {t('manager.aiAgents.siteImport.selectedCount', { count: selected.length, max: MAX_SITE_PAGES })}
      </p>
    </div>
  )
}

export function ImportProgress({ pages }: { pages: SiteImportPage[] }) {
  const { t } = useTranslation()
  const done = pages.filter((p) => FINISHED.includes(p.etat)).length
  return (
    <div className="min-w-0 space-y-3">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('manager.aiAgents.siteImport.progressCount', { done, total: pages.length })}
      </p>
      <Progress value={pages.length ? (done / pages.length) * 100 : 0} className="h-1.5" />
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {pages.map((p) => (
          <li key={p.url} className="flex items-start gap-3 py-2.5">
            <PageStateIcon etat={p.etat} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{p.titre}</p>
              <p
                className={`text-xs ${
                  p.etat === 'echec'
                    ? 'text-red-600 dark:text-red-400'
                    : p.etat === 'enregistree' || p.etat === 'prete'
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {p.etat === 'echec'
                  ? t(`manager.aiAgents.siteImport.reasons.${p.motif || 'lecture'}`)
                  : t(`manager.aiAgents.siteImport.states.${p.etat}`)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function PageStateIcon({ etat }: { etat: SiteImportPage['etat'] }) {
  const cls = 'mt-0.5 h-4 w-4 flex-shrink-0'
  if (etat === 'enregistree' || etat === 'prete')
    return <CheckCircle2 className={`${cls} text-emerald-600 dark:text-emerald-400`} />
  if (etat === 'deja') return <CheckCircle2 className={`${cls} text-slate-400`} />
  if (etat === 'echec') return <XCircle className={`${cls} text-red-500`} />
  if (etat === 'attente') return <Clock className={`${cls} text-slate-400`} />
  return <Loader2 className={`${cls} animate-spin text-blue-600`} />
}

/** "https://site.ca/services/tarifs" → "/services/tarifs" ("/" for the homepage). */
function pathOf(url: string) {
  try {
    return decodeURIComponent(new URL(url).pathname)
  } catch {
    return url
  }
}
